import { RumInitConfiguration, datadogRum } from "@datadog/browser-rum";
import { App } from "@capacitor/app";
import {
  DeploymentEnvironmentName,
  environmentConfig,
  isProductionNodeEnvironment,
} from "@src/appV2/environment";
import { ROLE_MAPPINGS } from "@src/appV2/FacilityUser/roles";
import { getCurrentBundleId } from "@src/mobile/utils/liveUpdate.adapter";
import { Platform } from "@src/mobile/utils/platform";
import { SessionType } from "src/modules/interface";

const datadogRumInitConfig: RumInitConfiguration = {
  applicationId: environmentConfig.REACT_APP_ADMIN_DATADOG_RUM_APPLICATION_ID,
  clientToken: environmentConfig.REACT_APP_ADMIN_DATADOG_RUM_CLIENT_TOKEN,
  service: "admin-web-app",
  defaultPrivacyLevel: "allow",
  version: __APP_VERSION__ || "1.0.0",
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  env: environmentConfig.REACT_APP_ENVIRONMENT_NAME,
  allowedTracingUrls: [
    environmentConfig.REACT_APP_API_MAIN_URL,
    /^https:\/\/.*\.prod\.clipboardhealth\.org/,
    /^https:\/\/.*\.clipboard\.health/,
  ],
  // `allowFallbackToLocalStorage` was removed in @datadog/browser-rum v7; the
  // localStorage fallback (needed for iOS Capacitor WebViews where cookies
  // can be blocked) is now the default behavior.
  // See: https://github.com/DataDog/browser-sdk/issues/480
  enableExperimentalFeatures: ["feature_flags"],
};

async function getCapacitorAppVersion(): Promise<string | undefined> {
  if (!Platform.isMobile) {
    return undefined;
  }

  try {
    const info = await App.getInfo();
    return info.version;
  } catch {
    return undefined;
  }
}

function isIPadOSDesktopMode(): boolean {
  // iPadOS Safari in desktop mode reports a Macintosh UA. Distinguish by
  // touch-point count, which a real Mac trackpad does not have.
  return (
    typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1
  );
}

function getOsFromUserAgent(userAgent: string): string {
  if (/iPhone|iPad|iPod/i.test(userAgent) || isIPadOSDesktopMode()) {
    return "ios";
  }
  if (/Android/i.test(userAgent)) {
    return "android";
  }
  if (/Windows/i.test(userAgent)) {
    return "windows";
  }
  if (/Mac OS X|Macintosh/i.test(userAgent)) {
    return "macos";
  }
  if (/Linux/i.test(userAgent)) {
    return "linux";
  }
  return "unknown";
}

export const initDatadogRum = async () => {
  if (
    isProductionNodeEnvironment() &&
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.PRODUCTION
  ) {
    const appVersion = await getCapacitorAppVersion();
    const liveUpdateBundleId = await getCurrentBundleId();
    const os = getOsFromUserAgent(navigator.userAgent);

    datadogRum.init({
      ...datadogRumInitConfig,
      beforeSend(event) {
        if (event.context) {
          event.context.app = "admin";
          event.context.os = os;
          event.context.screenWidth = window.screen.width;
          event.context.screenHeight = window.screen.height;
          event.context.platform = Platform.platform;
          event.context.isMobileApp = Platform.isMobile;
          if (appVersion) {
            event.context.appVersion = appVersion;
          }
          event.context.liveUpdateBundleId = liveUpdateBundleId;

          // Propagate identityProvider from RUM global context onto every
          // event so it survives into stored events. Monitors filter on
          // @context.identityProvider per-IdP error rate.
          const globalContext = datadogRum.getGlobalContext() as
            | { identityProvider?: unknown }
            | undefined;
          const identityProvider = globalContext?.identityProvider;
          if (
            typeof identityProvider === "string" &&
            event.context.identityProvider === undefined
          ) {
            event.context.identityProvider = identityProvider;
          }
        }
        return true;
      },
    });
  }
};

export const removeDatadogRUMUser = () => {
  datadogRum.clearUser();
};

export const identifyDatadogRumUser = (session: SessionType["session"]) => {
  const userProperties = getUserProperties(session);

  datadogRum.setUser({
    // As facility user names are not being searched for
    // we keep the facility's name instead of facility user's name for backward compatible
    name: session.profile?.name,
    email: session.user?.email,
    ...userProperties,
  });
};

export function setDatadogUserProperty(key: string, value: unknown) {
  datadogRum.setUserProperty(key, value);
}

function getSessionTypeProperties(session: SessionType["session"]) {
  if (session.type === "FACILITY") {
    return { facilityId: session.profile?.userId };
  }

  if (session.type === "AGENT") {
    return { agentId: session.profile?.userId };
  }

  return { adminId: session.profile?.userId };
}

function getFacilityRoleLabels(roles: string[] | undefined): string | undefined {
  if (!roles?.length) {
    return undefined;
  }
  return roles
    .map((roleId) => ROLE_MAPPINGS[roleId as keyof typeof ROLE_MAPPINGS]?.label ?? roleId)
    .join(", ");
}

function getUserProperties(session: SessionType["session"]) {
  if (session?.adminProfile) {
    return {
      type: "ADMIN",
      id: session.user?._id,
      sessionType: session.type,
      roles: session.adminProfile.roles,
      ...getSessionTypeProperties(session),
    };
  }

  const facilityRoles = session.user?.access;
  return {
    type: "FACILITY",
    id: session.user?._id,
    facilityId: session.profile?.userId,
    roles: facilityRoles,
    roleLabels: getFacilityRoleLabels(facilityRoles),
  };
}
